<template>
  <div class="user-presence inline-block"
       :class="[{'user-presence__modal-left': isModalLeft}, {'user-presence__multiple-users': !calid}]"
       v-on-clickaway="onPresenceClickaway"
       v-if="userHasPermissionToEdit">
    <button class="up__button no-print"
            :class="{'do-bttn': activeCalendarDate}"
            @click="open">
      {{ $t('vue2.presence.work_hours') }}
      <span class="icon-arrow-down5"></span>
    </button>
    <transition name="fadeInDown">
      <div class="up-modal text-dark" v-if="modal">
        <div class="up-modal__header">
          <h2 class="text-blue">
            <span v-if="!showWeekSelector">{{ $t('vue2.presence.set_work_hours') }}</span>
            <span v-else>{{ $t('vue2.presence.choose_weeks_for_work_hours') }}</span>
          </h2>
          <div v-if="!showWeekSelector && calid"
               class="hint--left"
               :aria-label="$t('vue2.presence.edit_default_times')">
            <div class="icon__bg-circle"
                 @click="openDefaultTimesSettings">
              <span class="icon-wrench"></span>
            </div>
          </div>
        </div>
        <div class="up-modal__content" :class="{'showweeks': showWeekSelector}">
          <div v-if="isLoading" style="margin-bottom: 100px">
            <Spinner color="blue"></Spinner>
          </div>
          <div class="up-modal__content-slider"
               :class="{'showweeks': showWeekSelector}"
               v-else>
            <div :class="{'up-modal__content-slider--weekend-with-hours': hasWeekendEvents && !showWeekSelector}">
              <div class="flex-table user-presence__table">
                <div class="flex-tr flex-tr__header">
                  <div class="flex-td"></div>
                  <div class="flex-td"><span class="text-normal">{{
                      $t('vue2.presence.work_hours_in_the_week')
                    }}:</span>
                    {{ durationAsHoursAndMin(totalDurationWeek) }}
                  </div>
                </div>
                <div class="flex-tr tr-weekdays-hours"
                     v-for="weekDay in weekDays">
                  <div class="flex-td flex-1 capitalize user-presence__table__first">
                    <div class="flex-td">
                      {{ $filters.dddd(weekDay) }}
                    </div>
                    <div class="flex-td">
                      <button @click="addEvent(weekDay)"
                              :class="{'inactive': lastEventOfDayIsCloseToMidnight(weekDay)}">
                        <span>+</span>
                      </button>
                    </div>
                  </div>
                  <div class="flex-td flex-3 time-changer">
                    <template v-for="event in events">
                      <template v-if="compareDates(event.start, weekDay)">
                        <EditEvent
                          :key="event.id"
                          :event="event"
                          @updateStart="updateStart"
                          @updateEnd="updateEnd"
                          @delete="deleteEvent"
                          @duration="setEventDurations">
                        </EditEvent>
                      </template>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <WeekPicker
                :date="activeCalendarDate"
                :calid="calid"
                @weeks="setPastedWeeks"
                @isWithinCalRange="setCurrentDateIsNotWithinCalendarRange">
              </WeekPicker>
            </div>
          </div>
        </div>
        <!-- FOOTER -->
        <div class="up-modal__footer">
          <button
            class="bg-white"
            @click="close">
            {{ $t('vue2.presence.cancel') }}
          </button>
          <div>
            <div v-if="intersections.length < 1">
              <template v-if="!currentDateIsNotWithinCalendarRange">
                <button @click="showWeekSelector = !showWeekSelector">
                  <span v-if="!showWeekSelector">{{ $t('vue2.presence.copy_to_multiple_weeks') }}</span>
                  <span v-else>{{ $t('vue2.presence.back') }}</span>
                </button>
              </template>
              <button :class="{'bttn-passive': true}"
                      class="bg-white"
                      v-if="calid || pasteInWeeks.length > 0"
                      @click="savePresence">
                {{ $t('vue2.presence.save') }}
              </button>
            </div>
            <div v-else>
              <p class="text-danger">{{ $t('vue2.presence.times_may_not_overlap') }}</p>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <DModal v-if="isShowSettings"
            :show-close="false"
            :isBgOverlay="true"
            :appendToBody="true"
            @close="closeDefaultTimesSettings">
      <div class="d-modal text-dark" slot="insertComponent" style="width: 400px" @click.stop>
        <div class="d-modal__header">
          <h2>{{ $t('vue2.presence.default_work_hours') }}</h2>
          <div class="d-modal__close icon__bg-circle" @click="closeDefaultTimesSettings">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="d-modal__content text-dark">
          <label class="d-input marg-1-bottom">{{ $t('vue2.standard_settings.set_default_start_and_endtime') }}</label>
          <div class="d-input-group-flex">
            <TimeChanger
              addClass="presence-default-times"
              :showIcon="true"
              :inputWidth="80"
              :start="standardStartTime"
              :end="standardEndTime"
              :isFiveMinutesInterval="false"
              @updateStart="setStandardStartTime"
              @updateEnd="setStandardEndTime">
            </TimeChanger>
          </div>
        </div>
        <div class="d-modal__buttons pad-1">
          <button class="bttn bttn-simple marg-1-right" @click="closeDefaultTimesSettings">
            {{ $t('vue2.common.cancel') }}
          </button>
          <button class="bttn submit-button" @click="submitStandardSettings">
            {{ $t('vue2.common.save') }}
          </button>
        </div>
      </div>
    </DModal>
  </div>
</template>

<script>
import docendo from '../../../docendo'
import EditEvent from './PresenceUserEditEvent.vue'
import WeekPicker from './WeekPicker.vue'
import Spinner from '../../common/Spinner'
import DModal from '../../common/DModal'
import TimeChanger from '../../TimeChanger'
import eventbus from '../../../eventbus'
import CustomEvent from '../../../polyfills/CustomEvent' // eslint-disable-line no-unused-vars
import {mixin as clickaway} from 'vue-clickaway'
import Moment from 'moment'
import {extendMoment} from 'moment-range'

var moment = extendMoment(Moment)

const DEFAULT_START_HOUR = 8
const DEFAULT_START_MINUTES = 0
const DEFAULT_END_HOUR = 16
const DEFAULT_END_MINUTES = 0

export default {
  components: {EditEvent, WeekPicker, Spinner, DModal, TimeChanger},
  mixins: [clickaway],
  props: {
    calid: {
      type: Number
    },
    firstDayOfWeek: {
      default: 1
    },
    activeCalendarDate: {
      default: () => moment().format('YYYY-MM-DD')
    },
    isModalLeft: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: true,
      modal: false,
      durations: [],
      intersections: [],
      events: [],
      deletedEventIds: [],
      pasteInWeeks: [],
      showWeekSelector: false,
      currentDateIsNotWithinCalendarRange: false,
      isShowSettings: false,
      ignoreClickaway: false,
      standardStartTime: moment().set({'hour': DEFAULT_START_HOUR, 'minutes': DEFAULT_START_MINUTES}),
      standardEndTime: moment().set({'hour': DEFAULT_END_HOUR, 'minutes': DEFAULT_END_MINUTES})
    }
  },
  computed: {
    user () {
      return this.$store.state.user
    },
    userSettings () {
      return this.$store.state.userSettings
    },
    school () {
      return this.$store.state.activeSchool
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    isOwnCalendar () {
      return this.user && this.user.user_calendar_id === this.calid
    },
    userHasPermissionToEdit () {
      if (this.isOwnCalendar) {
        return this.userHasPermission('can_edit_own_presence')
      } else {
        return this.userHasPermission('can_edit_others_presence')
      }
    },
    weekDays () {
      let days = []
      // monday is 1
      let start = this.firstDayOfWeek
      for (let i = 0; i < 7; i++) {
        days.push(moment(this.activeCalendarDate).day(start))
        start++
      }
      return days
    },
    totalDurationWeek () {
      let arr = []
      this.events.forEach(event => {
        arr.push(event.tempDuration)
      })
      return arr.reduce((a, b) => {
        return a + b
      }, 0)
    },
    eventsToCreate () {
      let arr = this.events.filter(event => event.tempNew === true)
      let newArr = []
      arr.forEach(obj => {
        newArr.push({
          calendar_id: obj.calendar_id,
          start: obj.start,
          end: obj.end
        })
      })
      return newArr
    },
    eventsToUpdate () {
      let arr = this.events.filter(event => event.toUpdate === true)
      let newArr = []
      arr.forEach(obj => {
        newArr.push({
          id: obj.event_id,
          calendar_id: obj.calendar_id,
          start: obj.start,
          end: obj.end
        })
      })
      return newArr
    },
    hasWeekendEvents () {
      return this.events.some(event => {
        const startDate = moment(event.start)
        const dayOfWeek = startDate.day() // 6 = Saturday, 0 = Sunday
        return dayOfWeek === 0 || dayOfWeek === 6
      })
    }
  },
  watch: {
    events () {
      this.$emit('events', this.events)
    }
  },
  beforeDestroy () {
    window.clearTimeout(this.ignoreClickawayTimeout)
  },
  methods: {
    userHasPermission (val) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(val) > -1 || this.user.admin
      } else {
        return true
      }
    },
    setCurrentDateIsNotWithinCalendarRange (val) {
      this.currentDateIsNotWithinCalendarRange = val
    },
    durationAsHoursAndMin (inMinutes) {
      let hours = Math.floor(inMinutes / 60)
      let minutes = (inMinutes - (hours * 60))
      return hours + ' ' + this.$t('vue2.presence.hours') + ' ' + minutes + ' ' + this.$t('vue2.presence.minutes')
    },
    readCurrentEvents () {
      this.getAllEvents()
      this.cleanAllIntersections()
      this.checkAllWithAllForIntersection()
    },
    open () {
      if (this.calid) {
        this.readCurrentEvents()
      } else {
        // Opened Working Hours For Multiple Users
        this.isLoading = false
        this.events = []
      }
      this.modal = true
      this.pasteInWeeks = []
    },
    onPresenceClickaway (ev) {
      if (this.shouldIgnoreClickaway(ev)) {
        return
      }
      this.close()
    },
    shouldIgnoreClickaway (ev) {
      if (this.isShowSettings || this.ignoreClickaway) {
        return true
      }
      const path = (ev && (ev.path || (ev.composedPath && ev.composedPath()))) || []
      return path.some(el => {
        return el.classList && (
          el.classList.contains('d-modal') ||
          el.classList.contains('modal-wrapper') ||
          el.classList.contains('bg-overlay')
        )
      })
    },
    close () {
      if (this.shouldIgnoreClickaway()) {
        return
      }
      if (!this.modal) {
        return
      }
      this.$emit('close')
      this.deletedEventIds = []
      this.modal = false
      this.showWeekSelector = false
      this.cleanAllIntersections()
      if (this.calid) {
        this.getAllEvents()
      }
    },
    compareDates (firstDate, secondDate) {
      return moment(firstDate).format('YYYY-MM-DD') === moment(secondDate).format('YYYY-MM-DD')
    },
    lastEventOfDayIsCloseToMidnight (weekDay) {
      let dayEvents = this.events.filter(event => this.compareDates(event.start, weekDay))
      if (dayEvents.length > 1) {
        let lastEventEnd = moment(dayEvents[dayEvents.length - 1].end).utc()
        if ((moment(lastEventEnd).local().format('HH') * 1) === 23 && (moment(lastEventEnd).local().format('mm') * 1) > 49) {
          return true
        }
      }
      return false
    },
    addEvent (weekDay) {
      let start
      let end
      let dayEvents = this.events.filter(event => this.compareDates(event.start, weekDay))
      if (dayEvents.length < 1) {
        const times = this.getDefaultTimes()
        start = moment(weekDay).set({
          'hour': times.startHour,
          'minutes': times.startMinutes,
          'seconds': 0,
          'milliseconds': 0
        })
        end = moment(weekDay).set({
          'hour': times.endHour,
          'minutes': times.endMinutes,
          'seconds': 0,
          'milliseconds': 0
        })
      } else {
        if (this.lastEventOfDayIsCloseToMidnight(weekDay)) {
          return
        }
        let lastEventEnd = moment(dayEvents[dayEvents.length - 1].end)
        let add5Min = lastEventEnd.add(5, 'minutes')
        start = moment(weekDay).set(
          {
            'hour': add5Min.format('HH'),
            'minutes': add5Min.format('mm'),
            'seconds': 0,
            'milliseconds': 0
          })
        let add10Min = lastEventEnd.add(10, 'minutes')
        end = moment(weekDay).set(
          {
            'hour': add10Min.format('HH'),
            'minutes': add10Min.format('mm'),
            'seconds': 0,
            'milliseconds': 0
          })
      }
      let event = {
        calendar_id: this.calid,
        start_time: start.utc().toISOString(),
        end_time: end.utc().toISOString(),
        start: start.utc().toISOString(),
        end: end.utc().toISOString(),
        rendering: 'background',
        className: 'fc-bgevent__presence',
        deleted: false,
        tempId: Date.now(),
        tempDuration: 0,
        tempIntersect: false,
        tempNew: true
      }
      this.events.push(event)
      this.cleanAllIntersections()
      this.checkAllWithAllForIntersection()
    },
    deleteEvent (presenceEvent) {
      if (presenceEvent.event_id) {
        this.deletedEventIds.push(presenceEvent.event_id)
      }
      const idx = this.events.findIndex(event => event.tempId === presenceEvent.tempId)
      this.events.splice(idx, 1)
      this.cleanAllIntersections()
      this.checkAllWithAllForIntersection()
    },
    updateStart (tempIdAndStarTime) {
      const idx = this.events.findIndex(event => event.tempId === tempIdAndStarTime.tempId)
      this.events[idx].start = tempIdAndStarTime.startTime
      this.cleanAllIntersections()
      this.checkAllWithAllForIntersection()
    },
    updateEnd (tempIdAndEndTime) {
      const idx = this.events.findIndex(event => event.tempId === tempIdAndEndTime.tempId)
      this.events[idx].end = tempIdAndEndTime.endTime
      this.cleanAllIntersections()
      this.checkAllWithAllForIntersection()
    },
    setEventDurations (idAndDurationInMinutes) {
      const idx = this.events.findIndex(event => event.tempId === idAndDurationInMinutes.tempId)
      this.events[idx].tempDuration = idAndDurationInMinutes.asMinutes
    },
    cleanAllIntersections () {
      this.intersections = []
      for (let i = 0; i < this.events.length; i++) {
        this.events[i].tempIntersect = false
      }
    },
    checkTwoDatesForIntersection (idx1, idx2) {
      let range1 = moment.range(this.events[idx1].start, this.events[idx1].end)
      let range2 = moment.range(this.events[idx2].start, this.events[idx2].end)
      if (range1.intersect(range2)) {
        return true
      }
    },
    checkAllWithAllForIntersection () {
      for (let i = 0; i < this.events.length; i++) {
        for (let j = 0; j < this.events.length; j++) {
          if (i !== j) {
            if (this.checkTwoDatesForIntersection(i, j)) {
              this.events[i].tempIntersect = true
              this.intersections.push(this.events[j])
            }
          }
        }
      }
    },
    setPastedWeeks (weekArray) {
      this.pasteInWeeks = weekArray
    },
    getAllEvents () {
      const start = moment(this.activeCalendarDate)
        .day(this.firstDayOfWeek)
        .startOf('day')

      const end = moment(this.activeCalendarDate)
        .day(this.firstDayOfWeek + 6)
        .endOf('day')

      this.isLoading = true

      docendo.calendarEvents2(
        [this.calid],
        start,
        end,
        ['presence'],
        data => {
          this.isLoading = false
          const events = data.length > 0 ? JSON.parse(data) : []

          const sortedEvents = events.filter(event => event.event_type === 'presence').sort((a, b) => {
            return new Date(a.start) - new Date(b.start)
          })

          let tempIdCounter = 0

          this.events = sortedEvents.map(event => {
            return {
              ...event,
              rendering: 'background',
              className: 'fc-bgevent__presence',
              tempDuration: 0,
              tempId: tempIdCounter++,
              tempIntersect: false,
              toUpdate: true
            }
          })
        },
        error => {
          console.log(error)
        }
      )
    },
    savePresence () {
      // Is in calendar
      if (this.calid) {
        console.log('this.activeCalendarDate', this.activeCalendarDate)
        // Fix because some users didnt get first day of the week copied
        const dbDate = moment(this.activeCalendarDate).day(this.firstDayOfWeek).format('YYYY-MM-DD')
        console.log({dbDate})
        docendo.bulkPresenceEvent(this.calid, this.eventsToCreate, this.eventsToUpdate, this.deletedEventIds, (data) => {
          this.$emit('saved', data)
          // Copy + paste if weeks.length > 0
          if (this.pasteInWeeks.length > 0) {
            eventbus.toast = {text: this.$i18n.t('vue2.copycalendar.noticationMessage'), success: false}
            docendo.calendarCopyWeeks(this.calid, dbDate, this.pasteInWeeks, true, 'presence', (success) => {
            })
          }
        })
      } else {
        // Is for multiple
        const emitObj = {
          eventsToCreate: this.eventsToCreate,
          pasteInWeeks: this.pasteInWeeks
        }
        this.$emit('save', emitObj)
      }
      this.close(this.eventsToCreate)
    },
    getSavedTimesForCalendar (calid) {
      if (!calid) {
        return null
      }
      const saved = this.userSettings && this.userSettings.presenceWorkHoursDefaultTimes
      if (!saved || typeof saved !== 'object') {
        return null
      }
      const entry = saved[calid] || saved[String(calid)]
      if (!entry || !entry.start || !entry.end) {
        return null
      }
      return entry
    },
    getCalendarTimesMap () {
      const saved = (this.userSettings && this.userSettings.presenceWorkHoursDefaultTimes) || {}
      const map = {}
      Object.keys(saved).forEach(key => {
        if (saved[key] && saved[key].start && saved[key].end) {
          map[key] = saved[key]
        }
      })
      return map
    },
    getDefaultTimes () {
      const fallback = {
        startHour: DEFAULT_START_HOUR,
        startMinutes: DEFAULT_START_MINUTES,
        endHour: DEFAULT_END_HOUR,
        endMinutes: DEFAULT_END_MINUTES
      }
      const saved = this.getSavedTimesForCalendar(this.calid)
      if (!saved) {
        return fallback
      }
      const start = moment(saved.start)
      const end = moment(saved.end)
      if (!start.isValid() || !end.isValid()) {
        return fallback
      }
      return {
        startHour: start.hour(),
        startMinutes: start.minute(),
        endHour: end.hour(),
        endMinutes: end.minute()
      }
    },
    openDefaultTimesSettings () {
      const times = this.getDefaultTimes()
      this.standardStartTime = moment().set({'hour': times.startHour, 'minutes': times.startMinutes})
      this.standardEndTime = moment().set({'hour': times.endHour, 'minutes': times.endMinutes})
      this.isShowSettings = true
    },
    closeDefaultTimesSettings () {
      this.ignoreClickaway = true
      this.isShowSettings = false
      window.clearTimeout(this.ignoreClickawayTimeout)
      this.ignoreClickawayTimeout = window.setTimeout(() => {
        this.ignoreClickaway = false
      }, 300)
    },
    setStandardStartTime (payload) {
      this.standardStartTime = payload
    },
    setStandardEndTime (payload) {
      this.standardEndTime = payload
    },
    submitStandardSettings () {
      if (!this.calid) {
        return
      }
      const next = this.getCalendarTimesMap()
      next[this.calid] = {
        start: this.standardStartTime,
        end: this.standardEndTime
      }
      this.$store.dispatch('updateUserSettings', {
        presenceWorkHoursDefaultTimes: next
      })
      this.closeDefaultTimesSettings()
    }
  }
}
</script>

<style scoped>
@import '../../../assets/css/variables.css';

.user-presence {
  position: relative;
}

.flex-td.flex-1 {
  align-items: start;
}

.flex-tr > .flex-td:last-child {
  padding-right: 0px;
}

.up-modal {
  background-color: #fff;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, .21);
  border: 1px solid #e6e8eb;
  left: 0;
  position: absolute;
  top: calc(100% + 4px);
  width: 600px;
  z-index: 100;
  border-radius: 4px;
}

.user-presence__modal-left .up-modal {
  right: 0;
  left: initial;
}

.user-presence__multiple-users .up__button {
  background-color: #468ad7 !important;
  color: #fff !important;
  border: 1px solid #4e80b7 !important;
}

.user-presence__multiple-users .up-modal {
  left: auto;
  right: 0;
}

.user-presence .up_arrow {
  height: 0;
  width: 0;
  border: 6px solid transparent;
  border-bottom-color: #fafafa;
  left: 12px;
  position: absolute;
  top: -12px
}

.user-presence.user-presence__multiple-users .up_arrow {
  left: auto;
  right: 12px;
}

.up-modal__header {
  box-shadow: inset 0 -1px 0 0 #e6e8eb;
  border-radius: 4px 4px 0 0;
}

.up-modal__header, .up-modal__footer {
  align-items: center;
  background-color: #fafafa;
  display: flex;
  padding: 10px 20px;
  height: 50px;
  justify-content: space-between;
}

.up-modal__header .icon__bg-circle {
  color: #7a8188;
  flex-shrink: 0;
}

.up-modal__footer {
  box-shadow: inset 0 1px 0 0 #e6e8eb;
  border-radius: 0 0 4px 4px;
}

.up-modal__content {
  overflow: hidden;
}

.up-modal__content .flex-tr__header {
  height: 40px;
}

.up-modal__content.showweeks {
  overflow: hidden;
}

.up-modal__content-slider {
  display: flex;
  width: calc(600px * 2);
  transform: translateX(0);
  transition: transform 0.3s;
}

.up-modal__content-slider.showweeks {
  transform: translate3d(-600px, 0, 0);
}

.up-modal__content-slider > div {
  flex: 0 0 600px;
  max-height: 310px;
  overflow-x: hidden;
  overflow-y: auto;
}

.up-modal__content-slider > div.up-modal__content-slider--weekend-with-hours {
  max-height: 100%;
}

.up-modal__content-slider.showweeks > div {
  overflow: hidden;
}

.capitalize {
  text-transform: capitalize;
}

.time-changer {
  flex-direction: column;
  flex: 2;
  margin-bottom: -10px;
}

.noMore {
  border-color: var(--danger);
}

.text-normal {
  margin-right: 5px;
  font-weight: normal;
}

.flex-3 {
  flex: 3;
}

.d-modal {
  border-radius: 4px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.d-modal__close {
  position: absolute;
  top: 5px;
  right: 5px;
  font-size: 16px !important;
}

.d-modal__content {
  padding: 1.5rem 2rem;
}

.d-input-group-flex {
  display: flex;
  align-items: center;
}

.d-modal >>> .presence-default-times {
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  flex-wrap: nowrap !important;
}

.d-modal >>> .presence-default-times .input-with-icon {
  width: auto !important;
  flex: 0 0 auto;
}

.d-modal >>> .presence-default-times .timepicker-line {
  padding: 0 8px;
}

.d-modal__buttons {
  display: flex;
  justify-content: flex-end;
}

</style>



// WEBPACK FOOTER //
// PresenceUser.vue?7ce59f0c